import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowDown, ArrowUp, BadgeCheck, CalendarClock, Check, CheckCheck, FileText, Loader2, MessageSquareWarning, Minus, PenLine, Play,
  Send, ThumbsDown, ThumbsUp, Undo2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Money } from "@/components/Money";
import { StatusPill } from "@/components/tasks/TaskBits";
import { ApprovalStatus } from "@/components/approvals/ApprovalBits";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { CodeChip } from "@/components/approvals/ApprovalCode";

/** Pastel tint + saturated icon colour per type. */
export const TINT = {
  violet: { bg: "bg-violet-100", icon: "text-violet-700", solid: "bg-violet-600" },
  teal: { bg: "bg-teal-100", icon: "text-teal-700", solid: "bg-teal-600" },
  amber: { bg: "bg-amber-100", icon: "text-amber-700", solid: "bg-amber-500" },
  rose: { bg: "bg-rose-100", icon: "text-rose-700", solid: "bg-rose-600" },
  sky: { bg: "bg-sky-100", icon: "text-sky-700", solid: "bg-sky-600" },
  emerald: { bg: "bg-emerald-100", icon: "text-emerald-700", solid: "bg-emerald-600" },
  indigo: { bg: "bg-indigo-100", icon: "text-indigo-700", solid: "bg-indigo-600" },
  pink: { bg: "bg-pink-100", icon: "text-pink-700", solid: "bg-pink-600" },
};

const PRI = { High: ["text-rose-700 bg-rose-50", ArrowUp], Medium: ["text-amber-800 bg-amber-50", Minus], Low: ["text-emerald-700 bg-emerald-50", ArrowDown] };
export const Priority = ({ p }) => {
  const [cls, Icon] = PRI[p] || PRI.Medium;
  return <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", cls)} data-testid="priority-tag"><Icon className="h-3 w-3" />{p}</span>;
};

const AV = ["bg-violet-600", "bg-teal-600", "bg-amber-600", "bg-rose-600", "bg-sky-600", "bg-emerald-600", "bg-indigo-600", "bg-pink-600"];
export const Avatar = ({ id, name, className }) => {
  const h = [...(id || name || "")].reduce((s, c) => s + c.charCodeAt(0), 0);
  const ini = (name || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white", AV[h % AV.length], className)} data-blur title={name}>{ini}</span>;
};

export const useMotion = () => {
  const reduce = useReducedMotion();
  return reduce ? { initial: false, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0 }, reduce: true }
    : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, scale: 0.97 }, transition: { type: "spring", stiffness: 400, damping: 32 } };
};
export const stagger = (m, i) => ({ ...m, transition: { ...m.transition, delay: m.reduce ? 0 : Math.min(i, 12) * 0.04 } });

export const Count = ({ n, className }) => {
  const m = useMotion();
  return <motion.span key={n} initial={m.reduce ? false : { opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={m.transition} className={cn("inline-block tabular-nums", className)}>{n}</motion.span>;
};

/** Quick-action tile: squircle with pastel tint, bold icon, short label and a count badge. */
export const QuickTile = ({ icon: Icon, label, tint, count, onClick, testid }) => (
  <motion.button type="button" onClick={onClick} whileTap={{ scale: 0.92 }} whileHover={{ y: -2 }} className="flex min-h-[88px] flex-col items-center gap-2 rounded-2xl p-1 text-center" data-testid={testid}>
    <span className={cn("relative flex h-14 w-14 items-center justify-center rounded-[18px] shadow-sm", TINT[tint].bg)}>
      <Icon className={cn("h-6 w-6", TINT[tint].icon)} strokeWidth={2.4} />
      {count > 0 && <span className="absolute -right-1.5 -top-1.5 min-w-[20px] rounded-full bg-rose-600 px-1.5 text-center text-[11px] font-bold leading-5 text-white ring-2 ring-white"><Count n={count > 99 ? "99+" : count} /></span>}
    </span>
    <span className="text-[12px] font-semibold leading-tight text-slate-700">{label}</span>
  </motion.button>
);

const Due = ({ t }) => (
  <span className={cn("inline-flex items-center gap-1", t.overdue ? "font-semibold text-rose-700" : "")} data-testid={`due-${t.id}`}>
    <CalendarClock className="h-3.5 w-3.5" />{t.overdue && <span className="rounded-full bg-rose-600 px-1.5 text-[10px] font-bold uppercase text-white">Overdue</span>}{fmtDate(t.due_date)}
  </span>
);

const BTN = {
  go: "bg-emerald-600 text-white hover:bg-emerald-700",
  warn: "bg-amber-100 text-amber-900 hover:bg-amber-200",
  bad: "bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100",
  sky: "bg-sky-100 text-sky-800 hover:bg-sky-200",
};
const Btn = ({ icon: Icon, children, tone = "go", ...p }) => (
  <motion.div whileTap={{ scale: 0.95 }} className="flex-1">
    <Button type="button" {...p} className={cn("h-11 w-full gap-1.5 rounded-full text-sm font-semibold shadow-none", BTN[tone])}><Icon className="h-4 w-4" />{children}</Button>
  </motion.div>
);

/** Activity-style card: coloured icon circle, title, sub-line, status pill on the right, actions underneath. */
const Card = ({ tint, icon: Icon, to, title, sub, pill, extra, actions, testid, tag }) => (
  <motion.div whileHover={{ y: -2 }} className="rounded-[20px] bg-white p-4 shadow-[0_2px_12px_rgba(15,23,42,0.06)] ring-1 ring-slate-100" data-testid={testid}>
    <div className="flex items-start gap-3">
      <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full", TINT[tint].bg)}><Icon className={cn("h-5 w-5", TINT[tint].icon)} strokeWidth={2.4} /></span>
      <div className="min-w-0 flex-1">
        {tag && <div className={cn("text-[11px] font-bold uppercase tracking-wide", TINT[tint].icon)}>{tag}</div>}
        <Link to={to} className="block font-semibold leading-snug text-slate-900 hover:underline">{title}</Link>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">{sub}</div>
      </div>
      <div className="shrink-0">{pill}</div>
    </div>
    {extra && <div className="mt-3">{extra}</div>}
    {actions && <div className="mt-3 flex gap-2">{actions}</div>}
  </motion.div>
);

const Company = ({ x, multi }) => multi && x.company ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">{x.company}</span> : null;

export const TaskCard = ({ t, act, multi, chips, tint = "violet", tag }) => {
  const a = t.actions;
  const endorsed = t.reviewers.filter((r) => r.state === "endorsed").length;
  const buttons = act && [
    a.includes("endorse") && <Btn key="e" icon={Check} onClick={() => act(t, "endorse")} data-testid={`work-endorse-${t.id}`}>Endorse</Btn>,
    a.includes("note") && <Btn key="n" icon={MessageSquareWarning} tone="warn" onClick={() => act(t, "note")} data-testid={`work-note-${t.id}`}>Note</Btn>,
    a.includes("in_progress") && <Btn key="p" icon={Play} tone="sky" onClick={() => act(t, "in_progress")} data-testid={`work-progress-${t.id}`}>In progress</Btn>,
    a.includes("done") && <Btn key="d" icon={CheckCheck} onClick={() => act(t, "done")} data-testid={`work-done-${t.id}`}>Done</Btn>,
    a.includes("revise") && <motion.div key="r" whileTap={{ scale: 0.95 }} className="flex-1"><Button asChild className={cn("h-11 w-full rounded-full font-semibold", BTN.warn)} data-testid={`work-revise-${t.id}`}><Link to={`/tasks/${t.id}`}><PenLine className="h-4 w-4" /> Revise</Link></Button></motion.div>,
  ].filter(Boolean);
  return (
    <Card tint={tint} icon={chips ? BadgeCheck : FileText} to={`/tasks/${t.id}`} title={t.title} tag={tag} testid={`work-task-${t.id}`} pill={<StatusPill t={t} />}
      sub={<><Company x={t} multi={multi} /><span data-blur>{t.created_by.name} → {t.assignee.name}</span><Due t={t} /><Priority p={t.priority} /></>}
      extra={chips && (
        <div className="flex items-center gap-2">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-violet-100"><div className="h-full rounded-full bg-violet-600 transition-[width] duration-500" style={{ width: `${(100 * endorsed) / (t.reviewers.length || 1)}%` }} /></div>
          <span className="text-xs font-semibold text-violet-800" data-testid={`work-endorsed-${t.id}`}>{endorsed} of {t.reviewers.length} endorsed</span>
        </div>
      )}
      actions={buttons && buttons.length ? buttons : null} />
  );
};

export const RequestCard = ({ r, act, multi, tint = "teal", tag }) => {
  const a = r.actions;
  const buttons = act && [
    a.includes("recommend") && <Btn key="c" icon={ThumbsUp} onClick={() => act(r, "recommend")} data-testid={`work-recommend-${r.id}`}>Recommend</Btn>,
    a.includes("return") && <Btn key="t" icon={Undo2} tone="warn" onClick={() => act(r, "return")} data-testid={`work-return-${r.id}`}>Return</Btn>,
    a.includes("approve") && <Btn key="a" icon={Check} onClick={() => act(r, "approve")} data-testid={`work-approve-${r.id}`}>Approve</Btn>,
    a.includes("reject") && <Btn key="j" icon={ThumbsDown} tone="bad" onClick={() => act(r, "reject")} data-testid={`work-reject-${r.id}`}>Reject</Btn>,
  ].filter(Boolean);
  return (
    <Card tint={tint} icon={Send} to={`/approvals/${r.id}`} title={r.title} tag={tag} testid={`work-request-${r.id}`} pill={<ApprovalStatus r={r} />}
      sub={<><Company x={r} multi={multi} /><span className="font-bold text-slate-900"><Money value={r.amount} /></span><span data-blur>{r.applicant.name}</span><span>{r.category}</span><span data-blur>{r.number}</span><span>needed by {fmtDate(r.needed_by)}</span>{r.status === "approved" && <CodeChip code={r.approval_code} testid={`work-code-${r.id}`} />}</>}
      extra={r.recommendation && <div className="rounded-2xl bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid={`work-recommendation-${r.id}`}><span className="font-semibold" data-blur>Recommended by {r.recommendation.by}</span>{r.recommendation.comment && <>: “{r.recommendation.comment}”</>}</div>}
      actions={buttons && buttons.length ? buttons : null} />
  );
};

export const TextSheet = ({ ask, onClose, onSubmit }) => {
  const [text, setText] = useState("");
  return (
    <Sheet open={!!ask} onOpenChange={(o) => { if (!o) { setText(""); onClose(); } }}>
      <SheetContent side="bottom" className="pb-safe mx-auto max-w-xl rounded-t-[28px]" data-testid="work-text-sheet">
        <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-slate-200" />
        <SheetTitle>{ask?.title}</SheetTitle>
        <p className="mt-1 text-sm text-slate-500">{ask?.hint}</p>
        <Textarea autoFocus rows={4} value={text} onChange={(e) => setText(e.target.value)} className="mt-4 rounded-2xl text-base" placeholder="Required" data-testid="work-text-input" />
        <div className="mt-4 flex gap-2">
          <Button variant="outline" className="h-12 flex-1 rounded-full" onClick={() => { setText(""); onClose(); }} data-testid="work-text-cancel"><X className="h-4 w-4" /> Cancel</Button>
          <Button className={cn("h-12 flex-1 rounded-full font-semibold", ask?.kind === "reject" ? "bg-rose-600 text-white hover:bg-rose-700" : "bg-amber-500 text-white hover:bg-amber-600")} disabled={!text.trim()} onClick={() => { onSubmit(text.trim()); setText(""); }} data-testid="work-text-submit">{ask?.cta}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const EmptyArt = () => (
  <svg viewBox="0 0 200 130" className="mx-auto h-16 w-24" aria-hidden="true">
    <ellipse cx="100" cy="118" rx="64" ry="7" fill="#E2E8F0" />
    <rect x="62" y="22" width="76" height="92" rx="14" fill="#EDE9FE" />
    <rect x="82" y="14" width="36" height="16" rx="8" fill="#7C3AED" />
    {[46, 66, 86].map((y, i) => <g key={y}><circle cx="82" cy={y} r="6" fill={["#14B8A6", "#F59E0B", "#F43F5E"][i]} /><rect x="94" y={y - 4} width="32" height="8" rx="4" fill="#C4B5FD" /></g>)}
    <circle cx="148" cy="92" r="16" fill="#10B981" /><path d="M141 92l5 5 9-10" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Spinner = () => <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
